import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import {
  PaymentInfoActions,
  PaymentInfo
} from 'app/transaction-store/payment-info';
import { Observable } from 'rxjs';
import { Sdk } from '../../core/sdk.service';
import { tokenize } from '@idonatedev/idonate-sdk';

@Component({
  selector: 'payment-method',
  template: `
    <div [id]="containerId" class="payment-tokenizer-container"></div>
    <div class="payment-validation-error" *ngIf="fieldError" role="alert">
      {{ fieldError }}
    </div>
  `,
  styles: [
    `
      .payment-tokenizer-container {
        width: 100%;
        min-height: 46px;
      }
      .payment-validation-error {
        color: #c0392b;
        font-size: 0.85em;
        margin-top: 4px;
      }
    `
  ]
})
export class PaymentMethodComponent implements OnInit, OnDestroy {
  @Input() paymentType: 'credit_card' | 'bank_account' = 'credit_card';

  containerId: string;
  private tokenizer: tokenize.Tokenizer;
  errorMessage = '';
  // User-facing message shown beneath the tokenizer fields (e.g. "Please
  // enter your CVV" after Google Wallet autofills it with masked stars).
  fieldError = '';
  isInitialized = false;

  @Select() paymentInfo$: Observable<PaymentInfo>;

  constructor(private sdk: Sdk, private store: Store) {
    // Generate unique container ID
    this.containerId = `payment-tokenizer-${Date.now()}`;
  }

  async ngOnInit() {
    try {
      // Create appropriate tokenizer based on payment type
      this.tokenizer = await this.sdk.createTokenizer(
        this.paymentType,
        this.containerId
      );

      this.isInitialized = true;

      // Register this component as the active payment component
      this.sdk.activePaymentComponent = this;

      // Set up event handlers
      this.tokenizer.on('validation', state => {
        if (state.isValid) {
          this.errorMessage = '';
          this.fieldError = '';
          this.store.dispatch(new PaymentInfoActions.SetTokenValidation(''));
          return;
        }

        // Surface a per-field message for fields the tokenizer has examined
        // and rejected. isEmpty:false means the iframe fired a validation
        // event for the field — without that signal we'd show errors on
        // initial render before the user has touched anything. Drives the
        // Google Wallet case where CVV gets autofilled with "***".
        const message = this.describeInvalidField(state);
        if (message) {
          this.fieldError = message;
          this.store.dispatch(new PaymentInfoActions.SetTokenValidation(message));
        }
      });

      this.tokenizer.on('error', error => {
        this.errorMessage = error.message || 'An error occurred';
        this.store.dispatch(
          new PaymentInfoActions.SetTokenValidation(this.errorMessage)
        );
      });

      this.tokenizer.on('cardTypeChange', data => {
        // Update the card type in the store for display purposes
        if (data && data.cardType) {
          this.store.dispatch(new PaymentInfoActions.SetCardType(data.cardType));
        }
      });

      this.tokenizer.on('ready', () => {
        this.errorMessage = '';
        this.fieldError = '';
      });
    } catch (error) {
      this.errorMessage = `Failed to initialize payment form: ${error.message ||
        error}`;
      this.store.dispatch(
        new PaymentInfoActions.SetTokenValidation(this.errorMessage)
      );
    }
  }

  ngOnDestroy() {
    // Clear the active component reference if it's this component
    if (this.sdk.activePaymentComponent === this) {
      this.sdk.activePaymentComponent = null;
    }

    // Clear card type when component is destroyed
    this.store.dispatch(new PaymentInfoActions.SetCardType(''));

    if (this.tokenizer) {
      try {
        this.tokenizer.destroy();
      } catch (error) {
        // Silently handle cleanup errors
      }
    }
  }

  /**
   * Public method to trigger tokenization
   * Called from GiveButtonComponent during submission
   */
  public async tokenize(paymentData: any): Promise<{ token: string }> {
    if (!this.tokenizer || !this.isInitialized) {
      throw new Error('Payment form not initialized');
    }

    try {
      const result = await this.tokenizer.tokenize(paymentData);
      // Store the token in the payment info state
      this.store.dispatch(new PaymentInfoActions.SetToken(result.token));
      return result;
    } catch (error) {
      this.errorMessage = error.message || 'Tokenization failed';
      this.store.dispatch(
        new PaymentInfoActions.SetTokenValidation(this.errorMessage)
      );
      throw error;
    }
  }

  /**
   * Get the payment type this component is configured for
   */
  public getPaymentType(): 'credit_card' | 'bank_account' {
    return this.paymentType;
  }

  /**
   * Public method to validate the form
   */
  public async validate(): Promise<boolean> {
    if (!this.tokenizer || !this.isInitialized) {
      return false;
    }

    try {
      const result = await this.tokenizer.validate();
      return result.isValid;
    } catch (error) {
      return false;
    }
  }

  /**
   * Public method to get full validation results with error messages
   */
  public async getValidationResult(): Promise<{isValid: boolean, errors: Array<{field: string, message: string}>}> {
    if (!this.tokenizer || !this.isInitialized) {
      return {
        isValid: false,
        errors: [{field: 'form', message: 'Payment form not initialized'}]
      };
    }

    try {
      return await this.tokenizer.validate();
    } catch (error) {
      return {
        isValid: false,
        errors: [{field: 'form', message: error.message || 'Validation failed'}]
      };
    }
  }

  /**
   * Public method to clear the form
   */
  public clear(): void {
    if (this.tokenizer && this.isInitialized) {
      this.tokenizer.clear();
      this.errorMessage = '';
      this.fieldError = '';
    }
  }

  private describeInvalidField(state: any): string {
    if (!state) {
      return '';
    }
    if (state.cvv && !state.cvv.isValid && !state.cvv.isEmpty) {
      return 'Please enter your CVV';
    }
    if (
      state.cardNumber &&
      !state.cardNumber.isValid &&
      !state.cardNumber.isEmpty
    ) {
      return 'Please check your card number';
    }
    if (state.expiry && !state.expiry.isValid && !state.expiry.isEmpty) {
      return 'Please check the expiration date';
    }
    return '';
  }
}
